import {MapContainer, Marker, Popup, TileLayer, useMap} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {useQueryClient} from '@tanstack/react-query';
import {CONNECTION_STATUS_CHOICES, QUERY_KEYS} from '../../../consts/consts';
import {useEffect, useState} from 'react';

// Cache for geocoded addresses to avoid repeated API calls
const geocodeCache = {};

// Geocode address using OpenStreetMap Nominatim API
const geocodeAddress = async (address) => {
    if (!address || address.trim() === '') {
        return null;
    }

    // Check cache first
    if (geocodeCache[address]) {
        return geocodeCache[address];
    }

    try {
        // OpenStreetMap Nominatim API (free, no API key needed)
        // Usage policy: max 1 request per second
        const encodedAddress = encodeURIComponent(address);
        const response = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${encodedAddress}&format=json&limit=1`,
            {
                headers: {
                    'User-Agent': 'MarchlMeteringApp/1.0' // Required by Nominatim usage policy
                }
            }
        );

        if (!response.ok) {
            console.warn(`Geocoding failed for address: ${address}`);
            return null;
        }

        const data = await response.json();
        if (data && data.length > 0) {
            const coords = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
            geocodeCache[address] = coords;
            return coords;
        }
    } catch (error) {
        console.warn(`Error geocoding address ${address}:`, error);
    }

    return null;
};

// Component to auto-fit map bounds to show all markers
const FitBoundsToMarkers = ({ coordinates }) => {
    const map = useMap();

    useEffect(() => {
        if (!coordinates || Object.keys(coordinates).length === 0) {
            return;
        }

        // Get all coordinate values
        const positions = Object.values(coordinates);

        if (positions.length === 0) {
            return;
        }

        // If only one marker, center on it with a reasonable zoom
        if (positions.length === 1) {
            map.setView(positions[0], 13);
            return;
        }

        // Calculate bounds from all positions
        const bounds = L.latLngBounds(positions);

        // Fit map to bounds with padding
        map.fitBounds(bounds, {
            padding: [50, 50], // 50px padding on all sides
            maxZoom: 15 // Don't zoom in too close
        });
    }, [map, coordinates]);

    return null;
};

const GatewaysMap = () => {
    const gatewaysObjects = useQueryClient().getQueryData([QUERY_KEYS.GATEWAYS_LINKED]);
    const [gatewayCoordinates, setGatewayCoordinates] = useState({});
    const [isGeocoding, setIsGeocoding] = useState(true);

    // Geocode all gateway addresses when component mounts or gateways change
    useEffect(() => {
        if (!gatewaysObjects || gatewaysObjects.length === 0) {
            setIsGeocoding(false);
            return;
        }

        const geocodeAllGateways = async () => {
            const coords = {};

            // Geocode addresses sequentially to respect API rate limit (1 req/sec)
            for (const gateway of gatewaysObjects) {
                if (gateway.address) {
                    const position = await geocodeAddress(gateway.address);
                    if (position) {
                        coords[gateway.id] = position;
                    }
                    // Wait 1 second between requests to respect Nominatim usage policy
                    await new Promise(resolve => setTimeout(resolve, 1100));
                }
            }

            setGatewayCoordinates(coords);
            setIsGeocoding(false);
        };

        geocodeAllGateways();
    }, [gatewaysObjects]);

    const markerIconEnabled = L.icon({
        iconUrl: '/icons/signal-enabled.svg',
        iconSize: [32, 32],
        iconAnchor: [16, 32]
    });
    const markerIconDisabled = L.icon({
        iconUrl: '/icons/signal-disabled.svg',
        iconSize: [32, 32],
        iconAnchor: [16, 32]
    });

    if (!gatewaysObjects || gatewaysObjects.length === 0) return null;

    // Center map on Germany (good default for German addresses)
    const defaultCenter = [51.1657, 10.4515]; // Geographic center of Germany

    // Calculate center based on geocoded gateways, or use default
    const mapCenter = Object.keys(gatewayCoordinates).length > 0
        ? Object.values(gatewayCoordinates)[0]
        : defaultCenter;

    return (
        <div style={{width: '35%'}}>
            <MapContainer
                center={mapCenter}
                zoom={6}
                style={{width: '100%', height: '56vh', marginTop: 8, marginRight: 40}}
            >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19}/>
                <FitBoundsToMarkers coordinates={gatewayCoordinates} />
                {gatewaysObjects.map((gw) => {
                    const position = gatewayCoordinates[gw.id];

                    // Only render marker if we have coordinates for this gateway
                    if (!position) return null;

                    return (
                        <Marker
                            key={gw.id}
                            position={position}
                            icon={gw.status.status === CONNECTION_STATUS_CHOICES.ONLINE
                                ? markerIconEnabled
                                : markerIconDisabled
                            }
                        >
                            <Popup>
                                <b>{gw.name}</b>
                                <br />
                                <small>{gw.address}</small>
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>
        </div>
    );
};

export default GatewaysMap;
